import { type VersionSummary, deleteVersion, listVersions, loadVersion } from '@/lib/storage/db';
import type { Fiche } from '@pharmafiche/schema';
import { useEffect, useState } from 'react';

interface VersionsPanelProps {
  ficheId: string;
  currentFiche: Fiche;
  onRestore: (fiche: Fiche) => void;
  onClose: () => void;
}

/** Right-rail panel listing versions for the current fiche with diff view. */
export function VersionsPanel({ ficheId, currentFiche, onRestore, onClose }: VersionsPanelProps) {
  const [versions, setVersions] = useState<VersionSummary[] | null>(null);
  const [diffVersion, setDiffVersion] = useState<{ name: string; fiche: Fiche } | null>(null);

  const refresh = () => {
    listVersions(ficheId)
      .then(setVersions)
      .catch(() => setVersions([]));
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: refresh closes over ficheId; re-run when it changes.
  useEffect(refresh, [ficheId]);

  const handleCompare = async (name: string) => {
    const f = await loadVersion(ficheId, name);
    if (f) setDiffVersion({ name, fiche: f });
  };

  const handleRestore = async (name: string) => {
    const f = await loadVersion(ficheId, name);
    if (!f) return;
    if (
      !window.confirm(
        `Restaurer « ${name} » ? L'état actuel sera mis dans l'historique d'annulation.`,
      )
    )
      return;
    onRestore(f);
  };

  const handleDelete = async (name: string) => {
    if (!window.confirm(`Supprimer la version « ${name} » ?`)) return;
    await deleteVersion(ficheId, name);
    refresh();
  };

  if (diffVersion) {
    return (
      <DiffPanel
        currentFiche={currentFiche}
        otherName={diffVersion.name}
        otherFiche={diffVersion.fiche}
        onBack={() => setDiffVersion(null)}
        onRestore={() => onRestore(diffVersion.fiche)}
      />
    );
  }

  return (
    <aside
      className="fixed top-0 right-0 bottom-0 w-[360px] bg-paper border-l-2 border-ink shadow-ink z-30 flex flex-col"
      aria-label="Versions de la fiche"
      data-testid="versions-panel"
    >
      <header className="flex items-center justify-between px-4 py-3 border-b border-ink/30">
        <h3 className="font-gloock text-[18px] text-ink">Versions</h3>
        <button
          type="button"
          onClick={onClose}
          aria-label="Fermer"
          className="text-ink hover:bg-ink/10 rounded-full w-7 h-7 grid place-items-center"
        >
          ✕
        </button>
      </header>

      <div className="flex-1 overflow-y-auto p-3 flex flex-col gap-2">
        {versions === null ? (
          <p className="font-caveat text-ink-soft text-[15px] text-center mt-2">Chargement…</p>
        ) : versions.length === 0 ? (
          <p className="font-caveat text-ink-soft text-[15px] text-center mt-4">
            Pas encore de version sauvegardée.
            <br />
            Utilise « ⬇️ Sauver une version » pour créer un point de restauration.
          </p>
        ) : (
          versions.map((v) => (
            <article
              key={v.name}
              className="border-2 border-ink/30 rounded-control bg-white p-3 flex flex-col gap-1"
              data-testid="version-row"
            >
              <div className="flex items-baseline justify-between gap-2">
                <span className="font-gloock text-[15px] text-ink">{v.name}</span>
                <span className="font-mono text-[11px] text-ink-soft">
                  {new Date(v.created_at).toLocaleString('fr-FR', {
                    day: '2-digit',
                    month: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              </div>
              <div className="flex gap-1.5 mt-1">
                <button
                  type="button"
                  onClick={() => handleCompare(v.name)}
                  className="px-2 py-1 bg-white text-ink border-2 border-ink rounded-control font-hand text-[12px] hover:bg-accent-blue/10"
                  data-testid="version-compare"
                >
                  Comparer
                </button>
                <button
                  type="button"
                  onClick={() => handleRestore(v.name)}
                  className="px-2 py-1 bg-accent-yellow text-ink border-2 border-ink rounded-control font-hand text-[12px] hover:translate-y-[-1px] transition-transform"
                  data-testid="version-restore"
                >
                  Restaurer
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(v.name)}
                  aria-label={`Supprimer ${v.name}`}
                  className="ml-auto px-2 py-1 bg-white text-warn-text border-2 border-ink rounded-control font-hand text-[12px] hover:bg-warn-bg"
                >
                  🗑️
                </button>
              </div>
            </article>
          ))
        )}
      </div>
    </aside>
  );
}

interface DiffPanelProps {
  currentFiche: Fiche;
  otherName: string;
  otherFiche: Fiche;
  onBack: () => void;
  onRestore: () => void;
}

function DiffPanel({ currentFiche, otherName, otherFiche, onBack, onRestore }: DiffPanelProps) {
  const left = JSON.stringify(otherFiche, null, 2);
  const right = JSON.stringify(currentFiche, null, 2);
  const rows = lineDiff(left, right);

  return (
    <aside
      className="fixed inset-0 z-40 bg-paper flex flex-col"
      aria-label={`Comparaison avec ${otherName}`}
      data-testid="versions-diff"
    >
      <header className="flex items-center justify-between px-4 py-3 border-b border-ink/30 gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            className="font-hand text-ink hover:underline underline-offset-4"
          >
            ← Retour aux versions
          </button>
          <h3 className="font-gloock text-[18px] text-ink">
            Comparaison · <span className="font-hand text-ink-soft">{otherName}</span> ↔ actuelle
          </h3>
        </div>
        <button
          type="button"
          onClick={onRestore}
          className="px-3 py-1.5 bg-accent-yellow text-ink border-[2.5px] border-ink rounded-control font-gloock text-[14px] shadow-ink-sm"
        >
          Restaurer cette version
        </button>
      </header>
      <div className="flex-1 overflow-auto px-4 py-3 grid grid-cols-2 gap-2 font-mono text-[12px] leading-[1.4]">
        <DiffColumn rows={rows.left} label={otherName} side="left" />
        <DiffColumn rows={rows.right} label="Actuelle" side="right" />
      </div>
    </aside>
  );
}

interface DiffRow {
  text: string;
  kind: 'same' | 'added' | 'removed' | 'empty';
  num?: number;
}

function DiffColumn({
  rows,
  label,
  side,
}: {
  rows: DiffRow[];
  label: string;
  side: 'left' | 'right';
}) {
  return (
    <div className="bg-white border-2 border-ink rounded-control overflow-hidden">
      <div className="bg-ink text-paper px-3 py-1 font-hand text-[13px] flex items-center justify-between">
        <span>{label}</span>
        <span className="font-mono text-[11px] opacity-70">
          {rows.filter((r) => r.kind !== 'empty').length} lignes
        </span>
      </div>
      <div className="overflow-auto max-h-[80vh]">
        {rows.map((r, i) => (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: diff rows are positional
            key={`${side}-${i}`}
            className="flex"
            style={{
              background:
                r.kind === 'added'
                  ? 'rgba(62, 154, 94, 0.10)'
                  : r.kind === 'removed'
                    ? 'rgba(211, 74, 74, 0.10)'
                    : undefined,
            }}
          >
            <span
              className="text-ink-soft w-10 text-right pr-2 select-none border-r border-ink/15 shrink-0"
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {r.num ?? ''}
            </span>
            <span
              className="px-2 whitespace-pre-wrap break-all"
              style={{
                color:
                  r.kind === 'added' ? '#3e9a5e' : r.kind === 'removed' ? '#d34a4a' : undefined,
              }}
            >
              {r.kind === 'added' ? `+ ${r.text}` : r.kind === 'removed' ? `- ${r.text}` : r.text}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Cheap line-diff. Walks both sides in lockstep and tags added/removed lines.
 * Good enough for a "what changed" preview without pulling in a 30 kB diff lib.
 */
function lineDiff(a: string, b: string): { left: DiffRow[]; right: DiffRow[] } {
  const al = a.split('\n');
  const bl = b.split('\n');
  const left: DiffRow[] = [];
  const right: DiffRow[] = [];
  let i = 0;
  let j = 0;
  let ln = 0;
  let rn = 0;
  while (i < al.length || j < bl.length) {
    const al_i = al[i];
    const bl_j = bl[j];
    if (al_i !== undefined && al_i === bl_j) {
      left.push({ text: al_i, kind: 'same', num: ++ln });
      right.push({ text: bl_j!, kind: 'same', num: ++rn });
      i++;
      j++;
    } else if (al_i !== undefined && (bl_j === undefined || !bl.slice(j).includes(al_i))) {
      left.push({ text: al_i, kind: 'removed', num: ++ln });
      right.push({ text: '', kind: 'empty' });
      i++;
    } else if (bl_j !== undefined) {
      left.push({ text: '', kind: 'empty' });
      right.push({ text: bl_j, kind: 'added', num: ++rn });
      j++;
    } else {
      break;
    }
  }
  return { left, right };
}
